import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

import { Checkbox } from '../../../components/ui/Checkbox';

// ── Key → Label mapping (ใช้ key ของ system_config) ─────────────────────────
const KEY_LABEL_MAP = {
  feature_moodboard_3d:        { label: 'Moodboard to 3D Room',           description: 'Enable/disable moodboard conversion tool',              beta: false },
  feature_cad_mood_room:       { label: 'CAD Mood Room Generator',         description: 'Enable/disable CAD file processing',                    beta: false },
  feature_sketch_interior:     { label: 'Sketch to Interior Render',       description: 'Enable/disable sketch rendering tool',                  beta: false },
  feature_space_planner:       { label: 'Space Planner',                   description: 'Enable/disable Space Planner tool',                     beta: false },
  feature_room_variation:      { label: 'Room Variation Generator',        description: 'Enable/disable variation generation',                   beta: false },
  feature_combine_styles:      { label: 'Combine Interior Styles',         description: 'Enable/disable style combination tool',                 beta: false },
  feature_furniture_materials: { label: 'Furniture and Materials Editing', description: 'Enable/disable Furniture and Materials Editing tool',   beta: false },
  feature_freestyle:           { label: 'Freestyle Design',                description: 'Enable/disable Freestyle design tool',                  beta: false },
  feature_site_concept:        { label: 'Site Concept',                    description: 'Enable/disable Site Concept tool',                      beta: false },
  feature_ground_up_room:      { label: 'Ground-Up Complete Room Design',  description: 'Enable/disable Ground-Up Complete Room Design tool',    beta: false },
};

const SystemConfiguration = ({ onSaveConfiguration }) => {
  // ── Feature Toggles (from Supabase) ──────────────────────────────────────
  const [featureToggles, setFeatureToggles] = useState({});
  const [loadingFeatures, setLoadingFeatures] = useState(true);
  const [savingFeatures, setSavingFeatures] = useState(false);

  // ── Subscription Plans ─────────────────────────────────────────────────────
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);   // plan object being edited
  const [editForm, setEditForm] = useState({});
  const [savingPlan, setSavingPlan] = useState(false);
  const [planCycleTab, setPlanCycleTab] = useState('one-time');

  // ── Maintenance Mode (from Supabase) ────────────────────────────────────────
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [loadingMaintenance, setLoadingMaintenance] = useState(true);
  const [maintenanceMessage, setMaintenanceMessage] = useState(
    "System maintenance in progress. We'll be back shortly."
  );

  // ══════════════════════════════════════════════════════════════════════════
  // FETCH on mount
  // ══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    fetchSubscriptionPlans();
    fetchFeatureToggles();
    fetchMaintenanceMode();
  }, []);

  // ── Fetch Maintenance Mode ──────────────────────────────────────────────────
  const fetchMaintenanceMode = async () => {
    setLoadingMaintenance(true);
    try {
      const { data, error } = await supabase
        .from('system_config')
        .select('key, value')
        .eq('key', 'maintenance_mode')
        .single();

      if (error) throw error;
      setMaintenanceMode(data?.value === 'true');
    } catch (err) {
      console.error('Error fetching maintenance mode:', err);
    } finally {
      setLoadingMaintenance(false);
    }
  };

  // ── Fetch Feature Toggles ─────────────────────────────────────────────────
  const fetchFeatureToggles = async () => {
    setLoadingFeatures(true);
    try {
      const { data, error } = await supabase
        .from('system_config')
        .select('key, value')
        .like('key', 'feature_%')
        .order('key');

      if (error) throw error;

      const toggled = {};
      (data || []).forEach(row => {
        toggled[row.key] = row.value === 'true';
      });
      setFeatureToggles(toggled);
    } catch (err) {
      console.error('Error fetching feature toggles:', err);
    } finally {
      setLoadingFeatures(false);
    }
  };

  // ── Fetch Subscription Plans ───────────────────────────────────────────────
  const fetchSubscriptionPlans = async () => {
    setLoadingPlans(true);
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;

      const cycleOrder = { 'one-time': 0, monthly: 1, annual: 2 };
      const nameOrder = ['free', 'solo', 'pro', 'team', 'enterprise'];
      const getNameRank = (name) => {
        const lower = (name || '').toLowerCase();
        const idx = nameOrder.findIndex((n) => lower.startsWith(n) || lower.includes(n));
        return idx !== -1 ? idx : 99;
      };
      const sorted = (data || []).sort((a, b) => {
        const cycleDiff =
          (cycleOrder[a.billing_cycle] ?? 9) - (cycleOrder[b.billing_cycle] ?? 9);
        if (cycleDiff !== 0) return cycleDiff;
        const nameDiff = getNameRank(a.name) - getNameRank(b.name);
        if (nameDiff !== 0) return nameDiff;
        return (a.price ?? 0) - (b.price ?? 0);
      });

      setPlans(sorted);
    } catch (err) {
      console.error('Error fetching subscription plans:', err);
    } finally {
      setLoadingPlans(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // HANDLERS — Feature Toggles
  // ══════════════════════════════════════════════════════════════════════════

  const handleFeatureToggle = (key) => {
    setFeatureToggles((prev) => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleSaveChanges = async () => {
    setSavingFeatures(true);
    try {
      // Save feature toggles
      const featureUpdates = Object.entries(featureToggles).map(([key, value]) => ({
        key,
        value: String(value),
        type: 'boolean',
        updated_at: new Date().toISOString(),
      }));

      // Save maintenance mode
      featureUpdates.push({
        key: 'maintenance_mode',
        value: String(maintenanceMode),
        type: 'boolean',
        updated_at: new Date().toISOString(),
      });

      // Save maintenance message
      featureUpdates.push({
        key: 'maintenance_message',
        value: maintenanceMessage,
        type: 'string',
        updated_at: new Date().toISOString(),
      });

      const { error } = await supabase
        .from('system_config')
        .upsert(featureUpdates, { onConflict: 'key' });

      if (error) throw error;

      onSaveConfiguration({ featureToggles, maintenanceMode, maintenanceMessage });
    } catch (err) {
      console.error('Error saving configuration:', err);
    } finally {
      setSavingFeatures(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // EDIT PLAN modal helpers
  // ══════════════════════════════════════════════════════════════════════════

  const openEditPlan = async (plan) => {
    setEditingPlan(plan);

    // Parse description JSON: { description, offer, feature[] } or legacy formats
    let featureList = [];
    let desc = '';
    let offer = '';
    if (plan.description) {
      try {
        const parsed = JSON.parse(plan.description);
        if (Array.isArray(parsed)) {
          // Legacy format: plain array of strings
          featureList = parsed.map((f) => (typeof f === 'string' ? f : String(f)));
        } else if (typeof parsed === 'object' && parsed !== null) {
          desc = parsed.description || parsed.tagline || '';
          offer = parsed.offer || parsed.highlight || '';
          const items = parsed.feature || parsed.features || parsed.list || [];
          featureList = Array.isArray(items)
            ? items.map((f) => (typeof f === 'string' ? f : String(f)))
            : [];
        }
      } catch {
        // Not JSON — treat as single plain text item
        featureList = [plan.description];
      }
    }
    // Ensure at least one empty field
    if (featureList.length === 0) featureList = [''];

    // If this is a team plan, fetch max_team_members from system_config
    let maxTeamMembers = '';
    const isTeamPlan = (plan.name || '').toLowerCase().includes('team') && !(plan.name || '').toLowerCase().includes('member');
    if (isTeamPlan) {
      try {
        const { data: configRow } = await supabase
          .from('system_config')
          .select('value')
          .eq('key', 'max_team_members')
          .maybeSingle();
        maxTeamMembers = configRow?.value || '';
      } catch { /* silent */ }
    }

    setEditForm({
      name: plan.name,
      price: String(plan.price ?? 0),
      billing_cycle: plan.billing_cycle || 'monthly',
      desc,
      offer,
      feature: featureList,
      max_credits: plan.max_credits != null ? String(plan.max_credits) : '',
      max_projects: plan.max_projects != null ? String(plan.max_projects) : '',
      max_storage: plan.max_storage != null ? String(plan.max_storage) : '',
      stripe_price_id: plan.stripe_price_id || '',
      is_active: plan.is_active ?? true,
      max_team_members: maxTeamMembers,
      _isTeamPlan: isTeamPlan,
    });
  };

  const closeEditPlan = () => {
    setEditingPlan(null);
    setEditForm({});
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  // ── Repeater helpers for list ────────────────────────────────────────────
  const handleFeatureItemChange = (index, value) => {
    setEditForm((prev) => {
      const updated = [...(prev.feature || [])];
      updated[index] = value;
      return { ...prev, feature: updated };
    });
  };

  const addFeatureItem = () => {
    setEditForm((prev) => ({
      ...prev,
      feature: [...(prev.feature || []), ''],
    }));
  };

  const removeFeatureItem = (index) => {
    setEditForm((prev) => {
      const updated = [...(prev.feature || [])];
      updated.splice(index, 1);
      if (updated.length === 0) updated.push('');
      return { ...prev, feature: updated };
    });
  };

  const handleSavePlan = async () => {
    if (!editingPlan) return;
    setSavingPlan(true);
    try {
      // Build description as JSON object { description, offer, feature[] }
      const cleanFeature = (editForm.feature || []).filter((f) => f.trim() !== '');
      const descObj = {
        description: (editForm.desc || '').trim(),
        offer: (editForm.offer || '').trim(),
        feature: cleanFeature,
      };
      const descriptionValue = JSON.stringify(descObj);

      const { error } = await supabase
        .from('subscription_plans')
        .update({
          name: editForm.name,
          price: parseFloat(editForm.price) || 0,
          billing_cycle: editForm.billing_cycle,
          description: descriptionValue,
          max_credits: editForm.max_credits ? parseInt(editForm.max_credits, 10) : null,
          max_projects: editForm.max_projects ? parseInt(editForm.max_projects, 10) : null,
          max_storage: editForm.max_storage ? parseInt(editForm.max_storage, 10) : null,
          stripe_price_id: editForm.stripe_price_id?.trim() || null,
          is_active: editForm.is_active,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingPlan.id);

      if (error) throw error;

      // Save max_team_members to system_config if this is a team plan
      if (editForm._isTeamPlan && editForm.max_team_members !== undefined) {
        const val = editForm.max_team_members ? String(editForm.max_team_members) : '5';
        await supabase
          .from('system_config')
          .upsert(
            { key: 'max_team_members', value: val, type: 'number', description: 'Maximum team members per team owner', updated_at: new Date().toISOString() },
            { onConflict: 'key' }
          );
      }

      closeEditPlan();
      await fetchSubscriptionPlans();
      onSaveConfiguration({ planUpdated: true });
    } catch (err) {
      console.error('Error updating plan:', err);
      console.error('Failed to update plan:', err.message);
    } finally {
      setSavingPlan(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // UTILS
  // ══════════════════════════════════════════════════════════════════════════

  const billingLabel = (cycle) => {
    if (cycle === 'annual') return '/month';
    if (cycle === 'one-time') return '';
    return '/month';
  };

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════

  return (
    <div className="space-y-6">
      {/* ── Header + Save ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">System Configuration</h2>
        <Button
          variant="default"
          iconName={savingFeatures ? 'Loader2' : 'Save'}
          iconPosition="left"
          onClick={handleSaveChanges}
          disabled={savingFeatures || loadingFeatures}
        >
          {savingFeatures ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* ── Feature Toggles ────────────────────────────────────────────────── */}
      <div className="bg-card rounded-lg border border-border shadow-soft p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="ToggleLeft" size={20} className="text-accent" />
          Feature Toggles
        </h3>

        {loadingFeatures ? (
          <div className="flex items-center gap-2 py-6 text-muted-foreground text-sm justify-center">
            <Icon name="Loader2" size={18} className="animate-spin" />
            Loading feature toggles...
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(KEY_LABEL_MAP).map(([key, meta]) => (
              <Checkbox
                key={key}
                label={meta.label}
                description={meta.description}
                checked={featureToggles[key] ?? false}
                onChange={() => handleFeatureToggle(key)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Maintenance Mode ───────────────────────────────────────────────── */}
      <div className="bg-card rounded-lg border border-border shadow-soft p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="AlertTriangle" size={20} className="text-warning" />
          Maintenance Mode
        </h3>
        <div className="space-y-4">
          <Checkbox
            label="Enable Maintenance Mode"
            description="Temporarily disable user access to the platform"
            checked={maintenanceMode}
            onChange={() => setMaintenanceMode(!maintenanceMode)}
          />
          {maintenanceMode && (
            <Input
              label="Maintenance Message"
              type="text"
              value={maintenanceMessage}
              onChange={(e) => setMaintenanceMessage(e?.target?.value)}
              description="Message displayed to users during maintenance"
            />
          )}
        </div>
      </div>

      {/* ── Subscription Configuration heading ────────────────────────────── */}
      <h2 id="subscription-configuration" className="text-xl font-semibold text-foreground pt-2">Subscription Configuration</h2>

      {/* ── Subscription Tiers (from DB) ───────────────────────────────────── */}
      <div className="bg-card rounded-lg border border-border shadow-soft p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Crown" size={20} className="text-accent" />
          Subscription Tiers
        </h3>

        {/* Billing Cycle Tabs */}
        <div className="flex items-center gap-1 mb-5 p-1 rounded-lg bg-muted/50 border border-border">
          {[
            { id: 'one-time', label: 'Free', icon: 'Gift' },
            { id: 'monthly', label: 'Monthly', icon: 'CalendarDays' },
            { id: 'annual', label: 'Annual', icon: 'CalendarRange' },
          ].map((tab) => {
            const count = plans.filter((p) => (p.billing_cycle || 'one-time') === tab.id).length;
            return (
              <button
                key={tab.id}
                onClick={() => setPlanCycleTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth flex-1 justify-center ${
                  planCycleTab === tab.id
                    ? 'bg-card text-foreground shadow-sm border border-border'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  planCycleTab === tab.id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {loadingPlans ? (
          <div className="flex items-center gap-2 py-6 text-muted-foreground text-sm justify-center">
            <Icon name="Loader2" size={18} className="animate-spin" />
            Loading subscription plans...
          </div>
        ) : (() => {
          const filtered = plans.filter((p) => (p.billing_cycle || 'one-time') === planCycleTab);
          return filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No plans found for this billing cycle.</p>
          ) : (
            <div className="space-y-4">
              {filtered.map((plan) => (
                <div key={plan.id} className="p-4 border border-border rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-foreground">{plan.name}</h4>
                      </div>
                      <p className="text-2xl font-bold text-primary mt-1">
                        ${parseFloat(plan.price || 0).toFixed(0)}
                        <span className="text-sm font-normal text-muted-foreground">
                          {billingLabel(plan.billing_cycle)}
                        </span>
                      </p>
                    </div>
                    <Button variant="outline" size="sm" iconName="Edit" onClick={() => openEditPlan(plan)}>
                      Edit
                    </Button>
                  </div>
                  {(() => {
                    let items = [];
                    let desc = '';
                    let offer = '';
                    if (plan.description) {
                      try {
                        const parsed = JSON.parse(plan.description);
                        if (Array.isArray(parsed)) {
                          items = parsed;
                        } else if (typeof parsed === 'object' && parsed !== null) {
                          desc = parsed.description || parsed.tagline || '';
                          offer = parsed.offer || parsed.highlight || '';
                          items = Array.isArray(parsed.feature) ? parsed.feature : (Array.isArray(parsed.list) ? parsed.list : (Array.isArray(parsed.features) ? parsed.features : []));
                        }
                      } catch { /* not JSON */ }
                    }
                    return (
                      <>
                        {desc && (
                          <p className="text-sm text-muted-foreground italic">{desc}</p>
                        )}
                        {offer && (
                          <p className="text-xs font-medium text-accent mt-1">{offer}</p>
                        )}
                        {items.length > 0 ? (
                          <ul className="space-y-1 mt-1">
                            {items.map((feat, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                                <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                                <span>{feat}</span>
                              </li>
                            ))}
                          </ul>
                        ) : !desc && !offer ? (
                          <p className="text-sm text-muted-foreground">{plan.description || '—'}</p>
                        ) : null}
                      </>
                    );
                  })()}
                  {(plan.max_credits || plan.max_projects || plan.max_storage) && (
                    <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                      {plan.max_credits != null && (
                        <span className="flex items-center gap-1">
                          <Icon name="Coins" size={12} />
                          {plan.max_credits} credits
                        </span>
                      )}
                      {plan.max_projects != null && (
                        <span className="flex items-center gap-1">
                          <Icon name="FolderOpen" size={12} />
                          {plan.max_projects} projects
                        </span>
                      )}
                      {plan.max_storage != null && (
                        <span className="flex items-center gap-1">
                          <Icon name="Image" size={12} />
                          {plan.max_storage} storage
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          EDIT PLAN MODAL
         ══════════════════════════════════════════════════════════════════════ */}
      {editingPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="bg-card rounded-xl border border-border shadow-floating w-full max-w-lg animate-in fade-in zoom-in-95">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                  <Icon name="Crown" size={20} className="text-accent" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Edit Plan</h2>
                  <p className="text-xs text-muted-foreground">Update subscription plan details</p>
                </div>
              </div>
              <button onClick={closeEditPlan} className="text-muted-foreground hover:text-foreground transition-smooth p-1 rounded-md hover:bg-muted">
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
              <Input
                label="Plan Name"
                type="text"
                value={editForm.name || ''}
                onChange={(e) => handleEditFormChange('name', e?.target?.value)}
              />

              {/* Stripe Price ID — hide for free (one-time) plans */}
              {editForm.billing_cycle !== 'one-time' && (
                <Input
                  label="Stripe Price ID"
                  type="text"
                  value={editForm.stripe_price_id || ''}
                  onChange={(e) => handleEditFormChange('stripe_price_id', e?.target?.value)}
                  description=""
                />
              )}

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Price ($)"
                  type="number"
                  value={editForm.price || ''}
                  onChange={(e) => handleEditFormChange('price', e?.target?.value)}
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Billing Cycle
                  </label>
                  <select
                    value={editForm.billing_cycle || 'monthly'}
                    onChange={(e) => handleEditFormChange('billing_cycle', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="one-time">One-time</option>
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
              </div>

              {/* Description — all plans */}
              <Input
                label="Description"
                type="text"
                value={editForm.desc || ''}
                onChange={(e) => handleEditFormChange('desc', e?.target?.value)}
                description=""
              />

              {/* Offer — hide for free (one-time) plans */}
              {editForm.billing_cycle !== 'one-time' && (
                <Input
                  label="Offer"
                  type="text"
                  value={editForm.offer || ''}
                  onChange={(e) => handleEditFormChange('offer', e?.target?.value)}
                  description=""
                />
              )}

              {/* Max Credits — hide for free (one-time) plans */}
              {editForm.billing_cycle !== 'one-time' ? (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <Input
                      label="Max Credits"
                      type="number"
                      value={editForm.max_credits || ''}
                      onChange={(e) => handleEditFormChange('max_credits', e?.target?.value)}
                      description=""
                    />
                    <Input
                      label="Max Projects"
                      type="number"
                      value={editForm.max_projects || ''}
                      onChange={(e) => handleEditFormChange('max_projects', e?.target?.value)}
                      description=""
                    />
                    <Input
                      label="Max Storage"
                      type="number"
                      value={editForm.max_storage || ''}
                      onChange={(e) => handleEditFormChange('max_storage', e?.target?.value)}
                      description=""
                    />
                  </div>
                  {editForm._isTeamPlan && (
                    <div className="grid grid-cols-1 gap-4 mt-4">
                      <Input
                        label="Max Team Members"
                        type="number"
                        value={editForm.max_team_members || ''}
                        onChange={(e) => handleEditFormChange('max_team_members', e?.target?.value)}
                        description=""
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Max Projects"
                    type="number"
                    value={editForm.max_projects || ''}
                    onChange={(e) => handleEditFormChange('max_projects', e?.target?.value)}
                    description=""
                  />
                  <Input
                    label="Max Storage"
                    type="number"
                    value={editForm.max_storage || ''}
                    onChange={(e) => handleEditFormChange('max_storage', e?.target?.value)}
                    description=""
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Feature
                </label>
                <div className="space-y-2">
                  {(editForm.feature || ['']).map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-5 text-center flex-shrink-0">{index + 1}.</span>
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => handleFeatureItemChange(index, e.target.value)}
                        placeholder={`Feature item ${index + 1}`}
                        className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                      <button
                        type="button"
                        onClick={() => removeFeatureItem(index)}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-error hover:bg-error/10 transition-smooth flex-shrink-0"
                        title="Remove item"
                      >
                        <Icon name="Trash2" size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addFeatureItem}
                    className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-smooth mt-1"
                  >
                    <Icon name="Plus" size={14} />
                    Add item
                  </button>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/30 rounded-b-xl">
              <Button variant="ghost" onClick={closeEditPlan} disabled={savingPlan}>
                Cancel
              </Button>
              <Button
                variant="primary"
                iconName={savingPlan ? 'Loader2' : 'Save'}
                iconPosition="left"
                onClick={handleSavePlan}
                disabled={savingPlan}
              >
                {savingPlan ? 'Saving...' : 'Save Plan'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemConfiguration;
